import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, FileText, Trash2, Edit2, BookOpen, RefreshCw,
  CheckCircle, XCircle, Loader2, AlertCircle, Eye, FolderOpen,
  ChevronRight, ChevronDown, Folder, File, X, FolderUp
} from 'lucide-react';
import EmptyState from '../../../components/EmptyState';

// ──────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────
// Supported file types — comprehensive list matching backend extraction service
// ──────────────────────────────────────────────────────
const SUPPORTED_EXTENSIONS = [
  // Documents
  '.pdf', '.txt', '.doc', '.docx', '.rtf', '.pptx', '.xlsx', '.xls',
  // Code
  '.py', '.pyw', '.pyi', '.js', '.jsx', '.mjs', '.cjs', '.ts', '.tsx', '.mts', '.cts',
  '.java', '.cpp', '.c', '.cc', '.cxx', '.h', '.hpp', '.hh',
  '.go', '.rs', '.rb', '.rake', '.gemspec', '.php', '.swift', '.kt', '.kts',
  '.scala', '.sc', '.r', '.lua', '.pl', '.pm', '.ex', '.exs', '.erl', '.hrl',
  '.hs', '.lhs', '.clj', '.cljs', '.cljc', '.edn', '.dart',
  '.sql', '.psql', '.plsql', '.m', '.groovy', '.gvy', '.gy', '.gsh', '.v', '.sv', '.svh',
  // Shell / Scripts
  '.sh', '.bash', '.zsh', '.fish', '.ksh', '.csh',
  '.ps1', '.psm1', '.psd1', '.ps1xml', '.bat', '.cmd',
  // IaC / HashiCorp / DevOps
  '.tf', '.tfvars', '.tfstate', '.hcl', '.nomad', '.sentinel',
  '.dockerfile', '.dockerignore', '.ansible', '.playbook',
  '.j2', '.jinja', '.jinja2', '.tpl', '.bicep', '.cdk',
  // Config / Data
  '.json', '.jsonc', '.json5', '.yaml', '.yml', '.xml', '.xsl', '.xslt', '.xsd', '.wsdl',
  '.toml', '.ini', '.cfg', '.conf', '.env', '.csv', '.tsv', '.properties',
  '.editorconfig', '.eslintrc', '.prettierrc', '.babelrc',
  '.gitignore', '.gitattributes', '.htaccess', '.proto', '.graphql', '.gql', '.prisma',
  // Markup / Documentation
  '.md', '.mdx', '.markdown', '.rst', '.rest', '.html', '.htm', '.xhtml',
  '.css', '.scss', '.sass', '.less', '.styl', '.tex', '.latex', '.bib',
  '.adoc', '.asciidoc', '.wiki', '.log', '.text',
  // Diagrams (text-based)
  '.svg', '.drawio', '.dio', '.mermaid', '.mmd', '.plantuml', '.puml', '.pu',
  // Images (AI Vision extraction)
  '.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.tiff', '.tif',
];

const FILE_CATEGORY_LABELS = {
  code: { label: 'code', color: 'bg-emerald-100 text-emerald-700' },
  infrastructure: { label: 'infrastructure', color: 'bg-orange-100 text-orange-700' },
  script: { label: 'script', color: 'bg-yellow-100 text-yellow-700' },
  config: { label: 'config', color: 'bg-purple-100 text-purple-700' },
  document: { label: 'document', color: 'bg-blue-100 text-blue-700' },
  diagram: { label: 'diagram', color: 'bg-pink-100 text-pink-700' },
  image: { label: 'image', color: 'bg-indigo-100 text-indigo-700' },
  other: { label: 'file', color: 'bg-gray-100 text-gray-600' },
};

function getFileCategory(name) {
  const ext = getFileExtension(name);
  const codeExts = new Set(['.py','.pyw','.pyi','.js','.jsx','.mjs','.cjs','.ts','.tsx','.java','.cpp','.c','.cc','.h','.hpp','.go','.rs','.rb','.php','.swift','.kt','.scala','.r','.lua','.pl','.ex','.erl','.hs','.clj','.dart','.sql','.m','.groovy','.v']);
  const iacExts = new Set(['.tf','.tfvars','.tfstate','.hcl','.nomad','.sentinel','.dockerfile','.dockerignore','.ansible','.j2','.jinja','.jinja2','.tpl','.bicep','.cdk']);
  const scriptExts = new Set(['.sh','.bash','.zsh','.fish','.ps1','.psm1','.psd1','.bat','.cmd','.ksh','.csh']);
  const configExts = new Set(['.json','.jsonc','.yaml','.yml','.xml','.toml','.ini','.cfg','.conf','.env','.csv','.tsv','.properties','.proto','.graphql','.prisma']);
  const diagramExts = new Set(['.svg','.drawio','.dio','.mermaid','.mmd','.plantuml','.puml','.pu']);
  const imageExts = new Set(['.png','.jpg','.jpeg','.webp','.gif','.bmp','.tiff','.tif']);
  if (codeExts.has(ext)) return 'code';
  if (iacExts.has(ext)) return 'infrastructure';
  if (scriptExts.has(ext)) return 'script';
  if (configExts.has(ext)) return 'config';
  if (diagramExts.has(ext)) return 'diagram';
  if (imageExts.has(ext)) return 'image';
  return 'document';
}

const MAX_FILES_PER_UPLOAD = 250;
const MAX_TOTAL_SIZE_MB = 100;
const MAX_FILE_SIZE_MB = 10;
const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// ──────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────
function getFileExtension(name) {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function isSupported(name) {
  return SUPPORTED_EXTENSIONS.includes(getFileExtension(name));
}

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function generateBatchId() {
  return `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Build a nested tree from flat items that have folderPath */
function buildFolderTree(items) {
  const root = { name: 'Root', fullPath: '', children: {}, items: [] };

  items.forEach(item => {
    if (item.folderPath) {
      const parts = item.folderPath.split('/').filter(Boolean);
      let current = root;
      let pathSoFar = '';
      parts.forEach(part => {
        pathSoFar = pathSoFar ? `${pathSoFar}/${part}` : part;
        if (!current.children[part]) {
          current.children[part] = { name: part, fullPath: pathSoFar, children: {}, items: [] };
        }
        current = current.children[part];
      });
      current.items.push(item);
    } else {
      root.items.push(item);
    }
  });

  return root;
}

// ──────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────

/** Recursively read files from a dropped directory entry */
async function readDirectoryEntries(entry, path = '') {
  const files = [];
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    // Attach relative path
    Object.defineProperty(file, '_relativePath', { value: path + file.name, writable: false });
    files.push(file);
  } else if (entry.isDirectory) {
    const dirReader = entry.createReader();
    let entries = [];
    // readEntries may not return all at once
    let batch;
    do {
      batch = await new Promise((resolve, reject) => dirReader.readEntries(resolve, reject));
      entries = entries.concat(batch);
    } while (batch.length > 0);

    for (const child of entries) {
      const childFiles = await readDirectoryEntries(child, path + entry.name + '/');
      files.push(...childFiles);
    }
  }
  return files;
}

/** Progress bar component */
function ProgressBar({ value, max, label, className = '' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className={className}>
      {label && <div className="text-xs text-gray-600 mb-1 flex justify-between"><span>{label}</span><span>{pct}%</span></div>}
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Collapsible folder tree node */
function FolderTreeNode({ node, depth = 0, onView, onRename, onDelete, onDeleteFolder, onRenameFolder, onRetryIndex, reindexing, getRagStatusBadge }) {
  const [open, setOpen] = useState(depth < 2); // auto-expand first 2 levels
  const hasChildren = Object.keys(node.children).length > 0;
  const hasItems = node.items.length > 0;
  const totalItems = countItems(node);

  if (!hasChildren && !hasItems) return null;

  return (
    <div>
      {/* Folder row (skip for root or depth 0 if it has no name) */}
      {depth > 0 && (
        <div
          className="flex items-center gap-2 w-full py-1.5 px-2 rounded-md hover:bg-blue-50 transition-colors"
          style={{ paddingLeft: `${depth * 16 + 4}px` }}
        >
          <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
            {hasChildren ? (
              open ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            ) : (
              <span className="w-4" />
            )}
            <Folder className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-800 truncate">{node.name}</span>
          </button>
          <Badge className="text-[10px] bg-gray-100 text-gray-500 flex-shrink-0">{totalItems} file{totalItems !== 1 ? 's' : ''}</Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRenameFolder(node); }}
            title={`Rename folder "${node.name}"`}
            className="h-7 px-1.5 flex-shrink-0"
          >
            <Edit2 className="w-3.5 h-3.5 text-gray-400 hover:text-blue-600" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDeleteFolder(node); }}
            title={`Delete folder "${node.name}" and all ${totalItems} files`}
            className="h-7 px-1.5 flex-shrink-0"
          >
            <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
          </Button>
        </div>
      )}

      {/* Children (folders + items) */}
      {(open || depth === 0) && (
        <div>
          {Object.values(node.children).map(child => (
            <FolderTreeNode
              key={child.name}
              node={child}
              depth={depth + 1}
              onView={onView}
              onRename={onRename}
              onDelete={onDelete}
              onDeleteFolder={onDeleteFolder}
              onRenameFolder={onRenameFolder}
              onRetryIndex={onRetryIndex}
              reindexing={reindexing}
              getRagStatusBadge={getRagStatusBadge}
            />
          ))}
          {node.items.map(item => (
            <KnowledgeItemRow
              key={item.id}
              item={item}
              depth={depth + 1}
              onView={onView}
              onRename={onRename}
              onDelete={onDelete}
              onRetryIndex={onRetryIndex}
              reindexing={reindexing}
              getRagStatusBadge={getRagStatusBadge}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function countItems(node) {
  let count = node.items.length;
  Object.values(node.children).forEach(child => { count += countItems(child); });
  return count;
}

/** Collect all item IDs recursively from a folder node */
function collectAllItemIds(node) {
  const ids = node.items.map(item => item.id);
  Object.values(node.children).forEach(child => { ids.push(...collectAllItemIds(child)); });
  return ids;
}

/** Single knowledge item row */
function KnowledgeItemRow({ item, depth, onView, onRename, onDelete, onRetryIndex, reindexing, getRagStatusBadge }) {
  const cat = getFileCategory(item.fileName || '');
  const catInfo = FILE_CATEGORY_LABELS[cat] || FILE_CATEGORY_LABELS.other;
  const catLabel = t(`fileCategories.${catInfo.label}`) || catInfo.label;
  return (
    <div
      className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-gray-50 transition-all group"
      style={{ paddingLeft: `${depth * 16 + 20}px` }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <File className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
          <Badge className={`text-[10px] ${catInfo.color}`}>{catLabel}</Badge>
          {getRagStatusBadge(item)}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 ml-5">
          <span>{new Date(item.createdAt).toLocaleDateString()}</span>
          <span>·</span>
          <span>{humanSize(item.fileSize || 0)}</span>
          {item.ragVersion > 0 && (
            <>
              <span>·</span>
              <span>v{item.ragVersion}</span>
            </>
          )}
          {item.folderPath && (
            <>
              <span>·</span>
              <span className="text-gray-400 truncate max-w-[200px]" title={item.folderPath}>{item.fileName}</span>
            </>
          )}
        </div>
        {item.ragStatus === 'failed' && item.ragLastError && (
          <p className="text-xs text-red-600 mt-0.5 ml-5 truncate" title={item.ragLastError}>
            Error: {item.ragLastError}
          </p>
        )}
      </div>
      <div className="flex gap-0.5 items-center flex-shrink-0">
        {item.ragStatus === 'failed' && (
          <Button variant="ghost" size="sm" onClick={() => onRetryIndex(item)} disabled={reindexing[item.id]} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 px-1.5">
            {reindexing[item.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onView(item); }} title="View content" className="h-7 px-1.5">
          <Eye className="w-3.5 h-3.5 text-gray-400 hover:text-blue-600" />
        </Button>
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onRename(item); }} title="Rename" className="h-7 px-1.5">
          <Edit2 className="w-3.5 h-3.5 text-gray-400 hover:text-blue-600" />
        </Button>
        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} title="Delete" className="h-7 px-1.5">
          <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
        </Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────────────
export default function KnowledgeTraining() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Text paste state
  const [text, setText] = useState('');
  const [textName, setTextName] = useState('');

  // Single file upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [docName, setDocName] = useState('');
  const [uploading, setUploading] = useState(false);

  // Folder upload state
  const [folderFiles, setFolderFiles] = useState([]); // { file, relativePath, status, error }
  const [folderUploadActive, setFolderUploadActive] = useState(false);
  const [folderProgress, setFolderProgress] = useState({ current: 0, total: 0, currentFileName: '' });
  const [dragOver, setDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [skippedFiles, setSkippedFiles] = useState([]);
  const folderInputRef = useRef(null);
  const abortRef = useRef(false);

  // Saved knowledge state
  const [savedKnowledge, setSavedKnowledge] = useState([]);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [reindexing, setReindexing] = useState({});
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewContent, setViewContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  // Confirm dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState({
    title: '', message: '', onConfirm: null
  });

  // Folder rename state
  const [folderRenameOpen, setFolderRenameOpen] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null); // { node, fullPath }
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => { fetchSavedKnowledge(); }, []);

  // ──────────────────── Data Fetching ────────────────────
  const fetchSavedKnowledge = async () => {
    try {
      const response = await apiClient.get('/vault/items?type=training_text');
      const knowledgeItems = response.data.filter(item => item.intent === 'Knowledge');
      setSavedKnowledge(knowledgeItems);
    } catch (error) {
      console.error('Failed to fetch knowledge training:', error);
      toast.error(t('errors.loadFailed'));
    }
  };

  // ──────────────────── RAG Status Badge ────────────────────
  const getRagStatusBadge = (item) => {
    const status = item.ragStatus || (item.indexedForRag ? 'indexed' : 'not_indexed');
    switch (status) {
      case 'indexed':
        return <Badge className="text-[10px] bg-green-100 text-green-700 flex items-center gap-0.5"><CheckCircle className="w-3 h-3" />Indexed</Badge>;
      case 'indexing':
        return <Badge className="text-[10px] bg-blue-100 text-blue-700 flex items-center gap-0.5"><Loader2 className="w-3 h-3 animate-spin" />Indexing</Badge>;
      case 'updating':
        return <Badge className="text-[10px] bg-amber-100 text-amber-700 flex items-center gap-0.5"><RefreshCw className="w-3 h-3 animate-spin" />Updating</Badge>;
      case 'failed':
        return <Badge className="text-[10px] bg-red-100 text-red-700 flex items-center gap-0.5"><XCircle className="w-3 h-3" />Failed</Badge>;
      case 'deleted':
        return <Badge className="text-[10px] bg-gray-100 text-gray-500 flex items-center gap-0.5"><Trash2 className="w-3 h-3" />Deleted</Badge>;
      default:
        return <Badge className="text-[10px] bg-gray-100 text-gray-500 flex items-center gap-0.5"><AlertCircle className="w-3 h-3" />Not Indexed</Badge>;
    }
  };

  // ──────────────────── Text Submit ────────────────────
  const handleTextSubmit = async () => {
    if (!text.trim()) { toast.error(t('errors.uploadFailed')); return; }
    try {
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], `knowledge-${Date.now()}.txt`, { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', textName || `Knowledge - ${new Date().toLocaleDateString()}`);
      formData.append('description', `Category: Knowledge | ${text.substring(0, 100)}...`);
      formData.append('type', 'training_text');
      formData.append('tags', 'training,knowledge,text');
      formData.append('allowForAI', 'true');
      formData.append('intent', 'Knowledge');
      await apiClient.post('/vault/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('success.saved'));
      setText('');
      setTextName('');
      fetchSavedKnowledge();
    } catch (error) { toast.error(t('errors.saveFailed')); }
  };

  // ──────────────────── Single File Upload ────────────────────
  const handleFileUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', docName || selectedFile.name);
      formData.append('description', 'Category: Knowledge | Reference document');
      formData.append('type', 'training_text');
      formData.append('tags', 'training,knowledge,document');
      formData.append('allowForAI', 'true');
      formData.append('intent', 'Knowledge');
      await apiClient.post('/vault/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('success.uploaded'));
      setSelectedFile(null);
      setDocName('');
      fetchSavedKnowledge();
    } catch (error) { toast.error(t('errors.uploadFailed')); }
    finally { setUploading(false); }
  };

  // ──────────────────── Folder Upload Logic ────────────────────
  const validateAndSetFiles = useCallback((files) => {
    const errors = [];
    const skipped = [];
    const supported = [];

    files.forEach(f => {
      const name = f.name || f._relativePath || '';
      if (!isSupported(name)) {
        skipped.push(name);
        return;
      }
      if (f.size > MAX_FILE_SIZE_BYTES) {
        errors.push(`${name}: exceeds ${MAX_FILE_SIZE_MB} MB limit`);
        return;
      }
      supported.push(f);
    });

    if (supported.length > MAX_FILES_PER_UPLOAD) {
      errors.push(`Too many files: ${supported.length} selected, maximum is ${MAX_FILES_PER_UPLOAD}`);
    }

    const totalSize = supported.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_TOTAL_SIZE_BYTES) {
      errors.push(`Total size ${humanSize(totalSize)} exceeds ${MAX_TOTAL_SIZE_MB} MB limit`);
    }

    setValidationErrors(errors);
    setSkippedFiles(skipped);

    if (errors.length === 0 && supported.length > 0) {
      setFolderFiles(
        supported.slice(0, MAX_FILES_PER_UPLOAD).map(f => ({
          file: f,
          relativePath: f._relativePath || f.webkitRelativePath || f.name,
          status: 'pending', // pending | uploading | done | error
          error: null
        }))
      );
    } else if (supported.length === 0 && skipped.length > 0) {
      toast.error('No supported files found in the selected folder.');
    }
  }, []);

  const handleFolderSelect = useCallback((e) => {
    const fileList = Array.from(e.target.files || []);
    if (fileList.length === 0) return;
    // Attach _relativePath from webkitRelativePath
    fileList.forEach(f => {
      Object.defineProperty(f, '_relativePath', { value: f.webkitRelativePath || f.name, writable: false });
    });
    validateAndSetFiles(fileList);
  }, [validateAndSetFiles]);

  // Drag & drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const items = e.dataTransfer?.items;
    if (!items) return;

    const allFiles = [];
    const promises = [];

    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry?.() || items[i].getAsEntry?.();
      if (entry) {
        promises.push(readDirectoryEntries(entry));
      } else {
        // Fallback: plain file
        const file = items[i].getAsFile();
        if (file) {
          Object.defineProperty(file, '_relativePath', { value: file.name, writable: false });
          allFiles.push(file);
        }
      }
    }

    const results = await Promise.all(promises);
    results.forEach(arr => allFiles.push(...arr));

    if (allFiles.length > 0) {
      validateAndSetFiles(allFiles);
    }
  }, [validateAndSetFiles]);

  const startFolderUpload = async () => {
    if (folderFiles.length === 0) return;

    setFolderUploadActive(true);
    abortRef.current = false;
    const batchId = generateBatchId();
    let doneCount = 0;

    for (let i = 0; i < folderFiles.length; i++) {
      if (abortRef.current) break;

      const entry = folderFiles[i];
      setFolderProgress({ current: i + 1, total: folderFiles.length, currentFileName: entry.file.name });

      // Mark uploading
      setFolderFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f));

      try {
        // Derive folder path (everything except the filename)
        const parts = entry.relativePath.split('/');
        const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : '';

        const formData = new FormData();
        formData.append('file', entry.file);
        formData.append('title', entry.file.name);
        formData.append('description', `Category: Knowledge | Folder upload: ${folderPath || 'root'}`);
        formData.append('type', 'training_text');
        formData.append('tags', 'training,knowledge,document,folder-upload');
        formData.append('allowForAI', 'true');
        formData.append('intent', 'Knowledge');
        if (folderPath) formData.append('folderPath', folderPath);
        formData.append('uploadBatchId', batchId);

        await apiClient.post('/vault/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

        setFolderFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'done' } : f));
        doneCount++;
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || 'Upload failed';
        setFolderFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', error: msg } : f));
      }
    }

    setFolderUploadActive(false);
    if (doneCount > 0) {
      toast.success(`Uploaded ${doneCount} of ${folderFiles.length} files successfully`);
      fetchSavedKnowledge();
    }
    if (doneCount < folderFiles.length && !abortRef.current) {
      toast.error(`${folderFiles.length - doneCount} file(s) failed to upload`);
    }
    // Auto-clear selection after upload completes (all done or only some failed)
    if (doneCount === folderFiles.length || abortRef.current) {
      // All succeeded or user cancelled — clear everything
      clearFolderSelection();
    }
  };

  const cancelFolderUpload = () => {
    abortRef.current = true;
  };

  const clearFolderSelection = () => {
    setFolderFiles([]);
    setValidationErrors([]);
    setSkippedFiles([]);
    setFolderProgress({ current: 0, total: 0, currentFileName: '' });
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  // ──────────────────── Actions (Retry, Rename, Delete, View) ────────────────────
  const handleRetryIndexing = async (item) => {
    setReindexing(prev => ({ ...prev, [item.id]: true }));
    try {
      await apiClient.post(`/rag/reindex/${item.fileId}`);
      toast.success('Document re-indexed successfully');
      fetchSavedKnowledge();
    } catch (error) { toast.error('Failed to re-index document'); }
    finally { setReindexing(prev => ({ ...prev, [item.id]: false })); }
  };

  const openRenameDialog = (item) => { setItemToRename(item); setNewName(item.title); setRenameDialogOpen(true); };

  const handleRename = async () => {
    try {
      await apiClient.put(`/vault/items/${itemToRename.id}?title=${encodeURIComponent(newName)}`);
      toast.success(t('success.updated'));
      setRenameDialogOpen(false);
      fetchSavedKnowledge();
    } catch (error) { toast.error(t('errors.saveFailed')); }
  };

  const handleDelete = async (itemId) => {
    setConfirmDialogData({
      title: t('knowledge.deleteItem'),
      message: t('knowledge.deleteItemConfirm'),
      onConfirm: async () => {
        try {
          await apiClient.delete(`/vault/items/${itemId}`);
          toast.success('Item deleted successfully');
          fetchSavedKnowledge();
        } catch (error) {
          console.error('Delete failed:', error);
          toast.error('Failed to delete item');
        }
      }
    });
    setConfirmDialogOpen(true);
  };

  const handleDeleteFolder = async (folderNode) => {
    const ids = collectAllItemIds(folderNode);
    const count = ids.length;
    if (count === 0) return;

    setConfirmDialogData({
      title: t('knowledge.deleteFolder'),
      message: `Delete folder "${folderNode.name}" and all ${count} file${count !== 1 ? 's' : ''} inside it? This cannot be undone.`,
      onConfirm: async () => {
        let deleted = 0;
        let failed = 0;
        for (const id of ids) {
          try {
            await apiClient.delete(`/vault/items/${id}`);
            deleted++;
          } catch {
            failed++;
          }
        }
        if (deleted > 0) toast.success(`Deleted ${deleted} file${deleted !== 1 ? 's' : ''} from "${folderNode.name}"`);
        if (failed > 0) toast.error(`${failed} file${failed !== 1 ? 's' : ''} failed to delete`);
        fetchSavedKnowledge();
      }
    });
    setConfirmDialogOpen(true);
  };

  const openFolderRenameDialog = (folderNode) => {
    setFolderToRename(folderNode);
    setNewFolderName(folderNode.name);
    setFolderRenameOpen(true);
  };

  const handleRenameFolder = async () => {
    if (!folderToRename || !newFolderName.trim()) return;
    const trimmed = newFolderName.trim();
    if (trimmed === folderToRename.name) { setFolderRenameOpen(false); return; }
    if (trimmed.includes('/') || trimmed.includes('..')) {
      toast.error('Folder name cannot contain / or ..');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('oldPath', folderToRename.fullPath);
      formData.append('newName', trimmed);
      const resp = await apiClient.put('/vault/folder-rename', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Renamed to "${trimmed}" (${resp.data.updatedItems} files updated)`);
      setFolderRenameOpen(false);
      fetchSavedKnowledge();
    } catch (error) {
      console.error('Folder rename failed:', error);
      toast.error(error?.response?.data?.detail || 'Failed to rename folder');
    }
  };

  const openViewDialog = async (item) => {
    setLoadingContent(true);
    setViewDialogOpen(true);
    try {
      const response = await apiClient.get(`/vault/content/${item.fileId}`);
      setViewContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load content');
      setViewContent({ content: '', title: item.title, previewStatus: 'failed', previewType: 'unknown' });
    } finally { setLoadingContent(false); }
  };

  const getPreviewLabel = (previewType, previewStatus) => {
    if (previewStatus === 'failed') return { label: t('preview.notAvailable'), color: 'bg-gray-100 text-gray-600' };
    if (previewStatus === 'unsupported') return { label: t('preview.unsupported'), color: 'bg-gray-100 text-gray-600' };
    const labels = {
      docx: 'DOCX',
      pdf: 'PDF',
      pdf_vision: 'PDF (AI)',
      pasted: t('knowledge.pasteInfo'),
      txt: 'TXT',
      doc: 'DOC',
      rtf: 'RTF',
      spreadsheet: t('preview.spreadsheet'),
      presentation: t('preview.presentation'),
      svg: 'SVG',
      diagram: t('preview.diagram'),
      image: previewStatus === 'success' ? t('preview.imageExtracted') : t('preview.imageNotExtracted'),
    };
    const label = labels[previewType] || previewType?.toUpperCase() || '';
    const colorMap = {
      success: 'bg-green-50 text-green-700',
      partial: 'bg-amber-50 text-amber-700',
    };
    return { label, color: colorMap[previewStatus] || 'bg-blue-50 text-blue-700' };
  };

  // ──────────────────── Folder Tree Memo ────────────────────
  const folderTree = useMemo(() => buildFolderTree(savedKnowledge), [savedKnowledge]);
  const hasFolderItems = useMemo(() => savedKnowledge.some(i => i.folderPath), [savedKnowledge]);

  // ──────────────────── Stats for folder selection ────────────────────
  const folderStats = useMemo(() => {
    if (folderFiles.length === 0) return null;
    const totalSize = folderFiles.reduce((sum, f) => sum + f.file.size, 0);
    const done = folderFiles.filter(f => f.status === 'done').length;
    const errors = folderFiles.filter(f => f.status === 'error').length;
    return { count: folderFiles.length, totalSize, done, errors };
  }, [folderFiles]);

  // ──────────────────── RENDER ────────────────────
  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/personal/twin/train')}>
          <ArrowLeft className="w-4 h-4 mr-2" />{t('common.back')}
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">{t('training.knowledgeTraining')}</h1>
          <p className="text-gray-600 mt-1">{t('training.whatYouKnow')}</p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 rounded-2xl bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <BookOpen className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">{t('knowledge.knowledgeVsStyle')}</p>
            <p className="text-blue-800">
              Knowledge training provides information your Twin can <strong>cite, summarize, or reference</strong>.
              Use this for facts, FAQs, product docs, or any content you want your Twin to know about.
            </p>
          </div>
        </div>
      </Card>

      {/* ═══════════ Upload Section ═══════════ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Paste Text Card */}
        <Card className="p-6 rounded-2xl space-y-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-heading text-xl font-semibold">{t('knowledge.pasteInfo')}</h3>
          <p className="text-sm text-gray-600">Paste facts, notes, or any information you want your Twin to remember</p>
          <div>
            <Label>{t('knowledge.titleOptional')}</Label>
            <Input value={textName} onChange={e => setTextName(e.target.value)} placeholder="e.g., Product FAQ, Company Info" className="mt-1" />
          </div>
          <Textarea value={text} onChange={e => setText(e.target.value)} placeholder={t('placeholders.knowledgeContent')} rows={8} className="resize-none" />
          <Button onClick={handleTextSubmit} className="w-full bg-blue-600 hover:bg-blue-700" disabled={!text.trim()}>{t('knowledge.saveKnowledge')}</Button>
        </Card>

        {/* Upload Files or Folder Card */}
        <Card className="p-6 rounded-2xl space-y-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Upload className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-heading text-xl font-semibold">{t('knowledge.uploadFilesOrFolder')}</h3>
          <p className="text-sm text-gray-600">
            Upload documents, code, configs, diagrams, images, and more — individually or as entire folders
          </p>

          {/* Drag & Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
              dragOver
                ? 'border-blue-500 bg-blue-50 scale-[1.01]'
                : selectedFile
                  ? 'border-blue-600 bg-blue-50'
                  : folderFiles.length > 0
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 hover:border-blue-300'
            }`}
          >
            {folderFiles.length > 0 ? (
              <div className="space-y-2">
                <FolderOpen className="w-8 h-8 text-green-600 mx-auto" />
                <p className="text-sm font-medium text-gray-900">{folderStats.count} files ready to upload</p>
                <p className="text-xs text-gray-500">Total size: {humanSize(folderStats.totalSize)}</p>
              </div>
            ) : selectedFile ? (
              <div>
                <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{humanSize(selectedFile.size)}</p>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-900 font-medium">Drop files or folders here</p>
                <p className="text-xs text-gray-500 mt-1">or use the buttons below to select</p>
              </>
            )}
          </div>

          {/* Action Buttons Row */}
          {!folderUploadActive && folderFiles.length === 0 && !selectedFile && (
            <div className="flex gap-2">
              <label className="flex-1">
                <Button variant="outline" className="w-full cursor-pointer" asChild>
                  <span><FileText className="w-4 h-4 mr-2" />Select File</span>
                </Button>
                <input
                  type="file"
                  accept={SUPPORTED_EXTENSIONS.join(',')}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) { setSelectedFile(file); setDocName(file.name); }
                  }}
                />
              </label>
              <label className="flex-1">
                <Button variant="outline" className="w-full cursor-pointer" asChild>
                  <span><FolderUp className="w-4 h-4 mr-2" />Select Folder</span>
                </Button>
                <input
                  ref={folderInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFolderSelect}
                  {...{ webkitdirectory: "", mozdirectory: "", directory: "" }}
                />
              </label>
            </div>
          )}

          {/* Validation Errors */}
          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1">
              {validationErrors.map((err, i) => (
                <p key={i} className="text-xs text-red-700 flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 flex-shrink-0" />{err}</p>
              ))}
            </div>
          )}

          {/* Skipped Files */}
          {skippedFiles.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-medium text-amber-800 mb-1">{skippedFiles.length} unsupported file(s) skipped:</p>
              <p className="text-xs text-amber-700 truncate">{skippedFiles.slice(0, 5).join(', ')}{skippedFiles.length > 5 ? `, +${skippedFiles.length - 5} more` : ''}</p>
            </div>
          )}

          {/* Folder File List Preview */}
          {folderFiles.length > 0 && (
            <div className="space-y-2">
              <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-200 rounded-lg p-2">
                {folderFiles.map((entry, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                    {entry.status === 'done' ? (
                      <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                    ) : entry.status === 'error' ? (
                      <XCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                    ) : entry.status === 'uploading' ? (
                      <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin flex-shrink-0" />
                    ) : (
                      <File className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-gray-700 truncate flex-1" title={entry.relativePath}>{entry.relativePath}</span>
                    <span className="text-gray-400">{humanSize(entry.file.size)}</span>
                  </div>
                ))}
              </div>

              {/* Progress */}
              {folderUploadActive && (
                <div className="space-y-2">
                  <ProgressBar value={folderProgress.current} max={folderProgress.total} label={`Uploading: ${folderProgress.currentFileName}`} />
                  <ProgressBar value={folderFiles.filter(f => f.status === 'done').length} max={folderFiles.length} label="Overall Progress" />
                </div>
              )}

              {/* Folder Upload Actions */}
              <div className="flex gap-2">
                {!folderUploadActive ? (
                  <>
                    <Button variant="outline" className="flex-1" onClick={clearFolderSelection}>
                      <X className="w-4 h-4 mr-1" />Clear
                    </Button>
                    <Button
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      onClick={startFolderUpload}
                      disabled={validationErrors.length > 0}
                    >
                      <Upload className="w-4 h-4 mr-1" />Upload {folderFiles.length} Files
                    </Button>
                  </>
                ) : (
                  <Button variant="destructive" className="flex-1" onClick={cancelFolderUpload}>
                    Cancel Upload
                  </Button>
                )}
              </div>

              {/* Post-upload summary */}
              {!folderUploadActive && folderStats && folderStats.done > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-800">
                    {folderStats.done} file(s) uploaded successfully
                    {folderStats.errors > 0 && <span className="text-red-600 ml-1">({folderStats.errors} failed)</span>}
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearFolderSelection} className="ml-auto text-xs">Dismiss</Button>
                </div>
              )}
            </div>
          )}

          {/* Single File Upload Controls */}
          {selectedFile && folderFiles.length === 0 && (
            <>
              <div>
                <Label>{t('forms.nameOptional')}</Label>
                <Input value={docName} onChange={e => setDocName(e.target.value)} placeholder={t('training.documentNamePlaceholder')} className="mt-1" />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setSelectedFile(null); setDocName(''); }} variant="outline" className="flex-1">Clear</Button>
                <Button onClick={handleFileUpload} disabled={uploading} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {uploading ? t('knowledge.uploading') : t('knowledge.upload')}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* ═══════════ Saved Knowledge Section ═══════════ */}
      <Card className="p-6 rounded-2xl">
        <h3 className="font-semibold text-gray-900 mb-4">{t('knowledge.savedKnowledge')} ({savedKnowledge.length})</h3>

        {savedKnowledge.length > 0 ? (
          <div className="space-y-1">
            {/* Folder tree view for items with folderPath */}
            {hasFolderItems && (
              <FolderTreeNode
                node={folderTree}
                depth={0}
                onView={openViewDialog}
                onRename={openRenameDialog}
                onDelete={handleDelete}
                onDeleteFolder={handleDeleteFolder}
                onRenameFolder={openFolderRenameDialog}
                onRetryIndex={handleRetryIndexing}
                reindexing={reindexing}
                getRagStatusBadge={getRagStatusBadge}
              />
            )}

            {/* Flat items without folderPath (only shown if there are also folder items, to avoid duplication) */}
            {!hasFolderItems && savedKnowledge.map(item => (
              <KnowledgeItemRow
                key={item.id}
                item={item}
                depth={0}
                onView={openViewDialog}
                onRename={openRenameDialog}
                onDelete={handleDelete}
                onRetryIndex={handleRetryIndexing}
                reindexing={reindexing}
                getRagStatusBadge={getRagStatusBadge}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={BookOpen}
            title={t('emptyStates.noKnowledgeData')}
            description={t('emptyStates.noKnowledgeDesc')}
            actionLabel={t('emptyStates.uploadDocument')}
            onAction={() => document.querySelector('input[type="file"]')?.click()}
            secondaryText="💡 Tip: Include company info, product details, or any factual information your Twin needs"
          />
        )}
      </Card>

      {/* Tips Card */}
      <Card className="p-6 rounded-2xl bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2">Tips for Better Knowledge Training</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2"><span className="text-blue-600 mt-1">•</span><span>Upload entire documentation folders to bulk-import structured knowledge</span></li>
          <li className="flex items-start gap-2"><span className="text-blue-600 mt-1">•</span><span><strong>Code & IaC:</strong> Upload .py, .js, .ts, .go, .tf, .hcl, .sh, .ps1, and 100+ more formats</span></li>
          <li className="flex items-start gap-2"><span className="text-blue-600 mt-1">•</span><span><strong>Documents:</strong> PDF, DOCX, PPTX, XLSX — text is automatically extracted</span></li>
          <li className="flex items-start gap-2"><span className="text-blue-600 mt-1">•</span><span><strong>Diagrams & Images:</strong> SVG, Draw.io, PNG, JPG — AI vision extracts content from images</span></li>
          <li className="flex items-start gap-2"><span className="text-blue-600 mt-1">•</span><span>Include details and context — your Twin cites individual documents in responses</span></li>
        </ul>
      </Card>

      {/* ═══════════ Dialogs ═══════════ */}
      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('knowledge.renameItem')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('knowledge.newName')}</Label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('knowledge.newName')} className="mt-1" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleRename} className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={!newName.trim()}>Rename</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Folder Rename Dialog */}
      <Dialog open={folderRenameOpen} onOpenChange={setFolderRenameOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('knowledge.renameFolder')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Rename folder <strong>"{folderToRename?.name}"</strong> and update paths for all files inside it.
            </p>
            <div>
              <Label>{t('knowledge.newFolderName')}</Label>
              <Input
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                placeholder="Enter new folder name"
                className="mt-1"
                onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(); }}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setFolderRenameOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleRenameFolder} className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={!newFolderName.trim()}>{t('knowledge.renameFolder')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              {viewContent?.title || t('knowledge.knowledgeContent')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingContent ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading content...</span>
              </div>
            ) : viewContent ? (
              <>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <Badge className="bg-blue-100 text-blue-700">Knowledge</Badge>
                  {viewContent.language && <Badge className="bg-emerald-100 text-emerald-700">{viewContent.language}</Badge>}
                  {viewContent.category && viewContent.category !== 'document' && (
                    <Badge className={`${(FILE_CATEGORY_LABELS[viewContent.category] || FILE_CATEGORY_LABELS.other).color}`}>
                      {(FILE_CATEGORY_LABELS[viewContent.category] || FILE_CATEGORY_LABELS.other).label}
                    </Badge>
                  )}
                  <span>·</span>
                  <span>{viewContent.fileName}</span>
                  <span>·</span>
                  <span>{humanSize(viewContent.fileSize || 0)}</span>
                </div>
                {viewContent.previewType && (
                  <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPreviewLabel(viewContent.previewType, viewContent.previewStatus).color}`}>
                    {getPreviewLabel(viewContent.previewType, viewContent.previewStatus).label}
                  </div>
                )}
                {viewContent.previewStatus === 'failed' || viewContent.previewStatus === 'unsupported' || !viewContent.content ? (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Preview not available for this file format.</p>
                    <p className="text-sm text-gray-500 mt-1">The content has been indexed for your Twin to use, but cannot be displayed here.</p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 max-h-[50vh] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono leading-relaxed">{viewContent.content}</pre>
                  </div>
                )}
                <p className="text-xs text-gray-500 italic">This content is used by your Twin to answer questions and provide accurate information.</p>
              </>
            ) : null}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>{t('knowledge.close')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmDialogData.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">{confirmDialogData.message}</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmDialogOpen(false);
                if (confirmDialogData.onConfirm) confirmDialogData.onConfirm();
              }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
